import type { CollapseProps } from 'antd/es/collapse';
import Collapse from 'antd/es/collapse';
import cn from 'classnames';
import { useMemo } from 'react';
import { CountryFlag } from 'shared/src/CountryFlag';
import { getCountryCode } from 'shared/src/getCountryCode';

import { virtualAccountNumberFormatter } from 'utils/formatters/virtualAccountNumberFormatter';
import { hasValue } from 'utils/hasValue';

import {
  AccountStatus,
  useGetCollectionFundingInstructions,
  useGetVirtualBankAccountsAccountNumberSummary
} from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { usePermissions } from 'hooks/usePermissions';

import { formatAccountDataToDetails } from './formatAccountDataToDetails';
import { getFundingSection } from './getFundingSection';
import { getSummarySection } from './getSummarySection';

export type ACCOUNT_TRANSACTIONS_KNOWN_SECTIONS = 'summary' | 'funding';

type CollapseItemType = NonNullable<CollapseProps['items']>[number];

export interface CollapseItemWithTypedSection
  extends Omit<CollapseItemType, 'key'> {
  key?: ACCOUNT_TRANSACTIONS_KNOWN_SECTIONS;
}

export const AccountDetails = ({
  accountNumber,
  fromDate,
  toDate
}: {
  accountNumber: string;
  fromDate?: string;
  toDate?: string;
}) => {
  const organisationId = useOrganisationId();
  const { isPermitted } = usePermissions();
  const canSeeFunding = isPermitted('accounts:funding');

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const currentAccountData = useMemo(
    () => findAccountByAccountNumber(accountNumber),
    [accountNumber, findAccountByAccountNumber]
  );

  // Funding instructions
  const {
    data: { data: fundingInstructions } = {},
    isSuccess,
    refetch
  } = useGetCollectionFundingInstructions(Number(organisationId), {
    query: { enabled: canSeeFunding }
  });
  const accountFundingInstructionsList =
    accountNumber && isSuccess && fundingInstructions
      ? fundingInstructions[accountNumber]
      : undefined;
  const currentAccountFunding = accountFundingInstructionsList?.find(
    (item) => item.virtual_account_number === accountNumber
  );

  const {
    data: summary,
    isSuccess: accountSummaryIsSuccess,
    isError
  } = useGetVirtualBankAccountsAccountNumberSummary(
    Number(organisationId),
    accountNumber as unknown as number,
    { created_after: fromDate!, created_before: toDate! },
    {
      query: {
        enabled: Boolean(fromDate && toDate && organisationId && accountNumber)
      }
    }
  );

  const virtualAccountDetails = useMemo(() => {
    if (hasValue(summary)) {
      return formatAccountDataToDetails(summary.data);
    }
    return [];
  }, [summary]);

  const items: CollapseProps['items'] = useMemo(() => {
    const sections: CollapseProps['items'] = [];

    if (!hasValue(virtualAccountDetails) || !accountSummaryIsSuccess) {
      return [];
    }

    sections.push(getSummarySection(virtualAccountDetails, isError));

    if (canSeeFunding && currentAccountFunding) {
      sections.push(
        getFundingSection({
          accountNumber,
          fundingInstructions: accountFundingInstructionsList,
          refetch
        })
      );
    }

    return sections;
  }, [
    virtualAccountDetails,
    accountSummaryIsSuccess,
    isError,
    canSeeFunding,
    currentAccountFunding,
    accountNumber,
    accountFundingInstructionsList,
    refetch
  ]);

  if (!accountNumber || !currentAccountData) return null;

  const accountStatusLabel =
    currentAccountData.status === AccountStatus.suspended
      ? 'Suspended'
      : currentAccountData.status === AccountStatus.terminated
        ? 'Terminated'
        : undefined;

  return (
    <div
      role="complementary"
      aria-label="Account details"
      className="cool-shadow flex max-h-full w-full flex-col overflow-hidden rounded-2xl bg-white"
    >
      <h2 className="flex gap-1 px-6 py-3.5 font-bold">Details</h2>

      <div className="px-6 py-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-2 rounded-2xl bg-surface-selected p-3">
            <CountryFlag
              countryCode={getCountryCode(currentAccountData.currency_code)}
              className="!size-[22px]"
              svg
            />
            <div className="font-bold">
              {`${currentAccountData.name} No. ${virtualAccountNumberFormatter(accountNumber)}`}
            </div>
          </div>
          {accountStatusLabel ? (
            <div
              className={cn(
                'w-full rounded px-3 py-1 text-sm',
                currentAccountData.status === AccountStatus.suspended
                  ? 'bg-feedback-attention text-white'
                  : 'bg-border-default text-content-primary'
              )}
            >
              {accountStatusLabel}
            </div>
          ) : null}
        </div>
      </div>

      <div className="overflow-auto">
        <Collapse
          className="antd-details-collapse bg-white"
          expandIconPosition="end"
          accordion
          bordered={false}
          defaultActiveKey={['summary']}
          items={items}
        />
      </div>
    </div>
  );
};
